import React from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  CardActionArea,
  AppBar,
  Toolbar,
  Button,
} from '@mui/material';
import {
  ViewList as ViewListIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon,
  ReceiptLong as ReceiptLongIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const FeatureCard: React.FC<{
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}> = ({ title, description, icon, onClick, disabled = false }) => (
  <Card 
    sx={{ 
      height: '100%',
      opacity: disabled ? 0.7 : 1,
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: disabled ? 'none' : 'translateY(-4px)',
        boxShadow: disabled ? 1 : 4,
      },
    }}
  >
    <CardActionArea 
      onClick={onClick}
      disabled={disabled}
      sx={{ height: '100%' }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'primary.light',
              borderRadius: '50%',
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" component="h2" align="center">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            {description}
          </Typography>
        </Box>
      </CardContent>
    </CardActionArea>
  </Card>
);

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { username, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            RuStore API Server
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography>
              User: {username}
            </Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Welcome to RuStore API Server
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Select a feature to get started
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FeatureCard
              title="Callbacks List"
              description="View and manage RuStore payment callbacks"
              icon={<ViewListIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
              onClick={() => navigate('/callbacks')}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FeatureCard
              title="Invoices"
              description="Просмотр списка платежей и детализация статусов"
              icon={<ReceiptLongIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
              onClick={() => navigate('/invoices')}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FeatureCard
              title="Products & External Pay"
              description="Каталог продуктов и генерация внешней ссылки оплаты"
              icon={<StorefrontIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
              onClick={() => navigate('/products')}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FeatureCard
              title="User Management"
              description="Здесь будет новый функционал: Управление пользователями"
              icon={<PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
              disabled
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FeatureCard
              title="Analytics"
              description="Здесь будет новый функционал: Аналитика"
              icon={<AnalyticsIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
              disabled
            />
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Home; 
